import api from './api';

/**
 * Check if the browser supports push notifications.
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Get the current notification permission state.
 * @returns {'granted' | 'denied' | 'default' | 'unsupported'}
 */
export function getPermissionState() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Register the service worker and return the registration.
 */
export async function registerServiceWorker() {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }

  const registration = await navigator.serviceWorker.register('/sw.js');
  return registration;
}

/**
 * Fetch the VAPID public key from the backend.
 */
async function getVapidPublicKey() {
  const { data } = await api.get('/notifications/vapid-public-key');
  return data.publicKey;
}

/**
 * Convert a base64 URL-safe string to a Uint8Array (for applicationServerKey).
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications and register the subscription with the backend.
 * @returns {PushSubscription}
 */
export async function subscribeToPush() {
  const registration = await registerServiceWorker();
  const vapidPublicKey = await getVapidPublicKey();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });

  // Send subscription to backend
  const subJson = subscription.toJSON();
  await api.post('/notifications/push/subscribe', {
    endpoint: subJson.endpoint,
    keys: {
      p256dh: subJson.keys.p256dh,
      auth: subJson.keys.auth,
    },
  });

  return subscription;
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    const subJson = subscription.toJSON();

    // Unsubscribe browser-side first, then notify backend
    await subscription.unsubscribe();
    await api.delete('/notifications/push/subscribe', {
      data: { endpoint: subJson.endpoint },
    });
  }
}

/**
 * Check if the user is currently subscribed to push.
 */
export async function isSubscribedToPush() {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return Boolean(subscription);
  } catch {
    return false;
  }
}

/**
 * Send a test push notification (development only).
 */
export async function sendTestPush() {
  const { data } = await api.post('/notifications/push/test');
  return data;
}

/**
 * Get notification preferences.
 */
export async function getNotificationPreferences() {
  const { data } = await api.get('/notifications/preferences');
  return data.preferences;
}

/**
 * Update notification preferences.
 */
export async function updateNotificationPreferences(updates) {
  const { data } = await api.patch('/notifications/preferences', updates);
  return data.preferences;
}
